/**
 * persistScheduler.ts — Debounced, coalesced persistence for multiAccountState.
 *
 * W20 (APP_FEEL_2027_UPGRADE §11.6 V-AF-01): the store's auto-persist used to
 * call `saveState` on EVERY mutation. `recordAnswer` sits on that path, so each
 * answered question serialized the entire multi-account blob and wrote it to
 * localStorage synchronously on the hot path.
 *
 * This scheduler:
 *  - coalesces bursts of mutations into a single trailing write (debounced),
 *    so a rapid answer-streak produces ONE write instead of N;
 *  - flushes immediately on `beforeunload` (and on demand) so no debounced
 *    write is ever lost on tab close / logout / session end;
 *  - surfaces write FAILURES the moment they happen (anti-bandaid #3 — no
 *    silent `return OK`), instead of relying only on the 60s usage poll.
 *
 * NOTE: explicit one-shot writes at boot/reset/demo still go through
 * `saveState` directly in `settingsActions.ts` — those are critical
 * transitions that must land immediately and are intentionally NOT debounced.
 *
 * Backend note: in localStorage mode the write is synchronous under the hood.
 * When `USE_BACKEND` flips on, this scheduler must be revisited (the
 * `beforeunload` synchronous flush has no async equivalent).
 */

import type { MultiAccountStateV4 } from '../../accounts';
import { STORAGE_KEY } from '../../config/constants';
import { safeStorageWrite } from '../../utils/storageQuota';
import { error as logError } from '../../utils/logger';

/** Trailing debounce window. Long enough to coalesce an answer-streak, short
 *  enough that a normal pause persists well before the user navigates away. */
const DEBOUNCE_MS = 400;
let timer: ReturnType<typeof setTimeout> | null = null;
let pending: MultiAccountStateV4 | null = null;
let beforeUnloadBound = false;

// ── Write-failure observability ──────────────────────────────────────
type FailureListener = () => void;
const failureListeners = new Set<FailureListener>();

/** Subscribe to persist write-failures (e.g. quota exceeded). Returns an
 *  unsubscribe function. Used by StorageWarningBanner to react immediately. */
export function subscribePersistFailure(listener: FailureListener): () => void {
  failureListeners.add(listener);
  return () => failureListeners.delete(listener);
}
function notifyFailure() {
  failureListeners.forEach(l => {
    try {
      l();
    } catch {

      // a misbehaving listener must not break persistence
    }
  });
}

/** Synchronously serialize + write the given state. Returns success. */
function writeNow(state: MultiAccountStateV4): boolean {
  const serialized = JSON.stringify(state);
  const ok = safeStorageWrite(STORAGE_KEY, serialized);
  if (!ok) {
    logError('Persist failed: localStorage quota exceeded. Recent progress may not be saved.');
    notifyFailure();
  }
  return ok;
}
function ensureBeforeUnloadBound() {
  if (beforeUnloadBound || typeof window === 'undefined') return;
  beforeUnloadBound = true;
  // Guarantee the latest pending state is written before the tab goes away.
  window.addEventListener('beforeunload', () => {
    flushPersist();
  });
  // `pagehide` covers mobile/bfcache cases `beforeunload` can miss.
  window.addEventListener('pagehide', () => {
    flushPersist();
  });
}

/**
 * Schedule a debounced persist of the latest state. Repeated calls within the
 * debounce window collapse into a single trailing write of the newest state.
 */
export function schedulePersist(state: MultiAccountStateV4): void {
  ensureBeforeUnloadBound();
  pending = state;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    const toWrite = pending;
    pending = null;
    if (toWrite) writeNow(toWrite);
  }, DEBOUNCE_MS);
}

/**
 * Immediately write any pending state and cancel the debounce timer.
 * Safe to call when nothing is pending (no-op). Used on tab close, logout,
 * and other critical boundaries.
 */
export function flushPersist(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (pending) {
    const toWrite = pending;
    pending = null;
    writeNow(toWrite);
  }
}